/**
 * face-maker/hair.js — OPTIONAL hairstyle module for the face constructor.
 *
 * Isolation contract (Farid, 2026-07-17): hair is a separate tab that degrades
 * to NOTHING — if this file is missing, throws, or its assets fail to load, the
 * tab never appears and app.js keeps working untouched. app.js only calls
 * window.__fmHairModule.init(ctx) inside try/catch.
 *
 * Hairstyles are pre-fitted offline onto the GNM template scalp. Each vertex of
 * a style is bound to one scalp triangle (barycentric + offset in the triangle's
 * local frame), so the hair follows ANY head produced by the identity sliders:
 *
 *   v = w1·A + w2·B + w3·C + (ox·e1 + oy·b2 + oz·n) · oscale · (|B-A|/|B-A|₀)
 *
 * where A,B,C are the CURRENT deformed positions of the bound triangle and
 * e1 = norm(B-A), n = tri normal, b2 = n×e1. The |B-A|/|B-A|₀ ratio grows the
 * offset with the head so long styles keep their proportions.
 *
 * Asset layout (public/vendor/gnm/hair/): hair-manifest.json lists styles;
 * hair-<id>.bin = [tris u16·nt·3][per-vertex: triId u16, w1 u16, w2 u16,
 * ox,oy,oz i16]; thumb-<id>.png gallery previews. Loaded lazily per style.
 */
(function () {
    'use strict';

    window.__fmHairModule = { init: init };

    var ctx = null, THREE_ = null;
    var manifest = null, base = null, assetV = '';
    var styles = [];                    // manifest.styles
    var cache = {};                     // id → parsed {tris, triId, w1, w2, off, L0}
    var state = { s: -1, c: 1 };        // style index (-1 = none), color index
    var hairMesh = null, hairGeo = null, hairMat = null;
    var loading = 0;                    // in-flight style fetches (last click wins)
    var els = {};

    var HAIR_COLORS = ['#181310', '#3a2a1d', '#5d4030', '#7a4a32', '#a34724', '#c9a15f', '#e3c894', '#9b9b9b'];

    function ae(e, d) { try { if (ctx && ctx.ae) ctx.ae(e, d || {}); } catch (_) {} }
    function L(k, fb) { return ctx && ctx.L ? ctx.L(k, fb) : fb; }
    function el(tag, cls, txt) { var e = document.createElement(tag); if (cls) e.className = cls; if (txt != null) e.textContent = txt; return e; }

    function init(context) {
        ctx = context; THREE_ = ctx.THREE;
        assetV = ctx.assetV || '';
        loadManifest().then(function () {
            if (!styles.length) return;
            buildTab();
            ctx.onRecompute(rebuild);
            // state restored from a share link / saved face (app.js hands it over)
            var pend = ctx.pendingState;
            if (pend && typeof pend.s === 'number') {
                state.c = clampColor(pend.c);
                if (pend.s >= 0 && pend.s < styles.length) setStyle(pend.s, true);
                else markUI();
            } else markUI();
            window.__fmHair = api;
        }).catch(function (err) {
            // no manifest → no tab; the rest of the tool is untouched
            ae('fm_hair_manifest_err', { msg: (err && err.message || '').slice(0, 60) });
        });
    }

    function clampColor(c) { c = c | 0; return (c >= 0 && c < HAIR_COLORS.length) ? c : 1; }

    /* ───────── assets ───────── */
    function fetchFrom(b, name, asJson) {
        return fetch(b + 'hair/' + name + assetV, { mode: b.indexOf('//') !== -1 ? 'cors' : 'same-origin' })
            .then(function (r) {
                if (!r.ok) throw new Error(name + ' ' + r.status);
                return asJson ? r.json() : r.arrayBuffer();
            });
    }
    function fetchAsset(name, asJson) {
        // face assets already picked a working base — try it first, then the rest
        var bases = [];
        if (ctx.activeBase) bases.push(ctx.activeBase);
        (ctx.bases || []).forEach(function (b) { if (bases.indexOf(b) === -1) bases.push(b); });
        var attempt = Promise.reject(new Error('no bases'));
        bases.forEach(function (b) {
            attempt = attempt.catch(function () { return fetchFrom(b, name, asJson).then(function (r) { base = b; return r; }); });
        });
        return attempt;
    }
    function loadManifest() {
        return fetchAsset('hair-manifest.json', true).then(function (m) {
            manifest = m; styles = (m && m.styles) || [];
        });
    }
    function parseStyle(buf, entry) {
        var nt = entry.nt, nv = entry.nv;
        var tris = new Uint16Array(buf, 0, nt * 3);
        var rec = new Int16Array(buf, nt * 3 * 2, nv * 6);
        var triId = new Uint16Array(nv), w1 = new Float32Array(nv), w2 = new Float32Array(nv);
        var off = new Float32Array(nv * 3);
        for (var i = 0; i < nv; i++) {
            triId[i] = rec[i * 6] & 0xffff;
            w1[i] = (rec[i * 6 + 1] & 0xffff) / 65535;
            w2[i] = (rec[i * 6 + 2] & 0xffff) / 65535;
            off[i * 3] = rec[i * 6 + 3] * entry.oscale;
            off[i * 3 + 1] = rec[i * 6 + 4] * entry.oscale;
            off[i * 3 + 2] = rec[i * 6 + 5] * entry.oscale;
        }
        // neutral edge length of each bound triangle (from the face TEMPLATE)
        var tpl = ctx.template, ftris = ctx.tris;
        var L0 = new Float32Array(nv);
        for (var j = 0; j < nv; j++) {
            var t = triId[j] * 3, a = ftris[t] * 3, b = ftris[t + 1] * 3;
            var dx = tpl[b] - tpl[a], dy = tpl[b + 1] - tpl[a + 1], dz = tpl[b + 2] - tpl[a + 2];
            L0[j] = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
        }
        return { tris: tris, triId: triId, w1: w1, w2: w2, off: off, L0: L0, nv: nv, nt: nt };
    }

    /* ───────── geometry ───────── */
    function rebuild() {
        if (state.s < 0 || !hairGeo) return;
        var st = cache[styles[state.s].id];
        if (!st) return;
        var pos = ctx.pos, ftris = ctx.tris, out = hairGeo.attributes.position.array;
        var nv = st.nv;
        for (var i = 0; i < nv; i++) {
            var t = st.triId[i] * 3;
            var ia = ftris[t] * 3, ib = ftris[t + 1] * 3, ic = ftris[t + 2] * 3;
            var ax = pos[ia], ay = pos[ia + 1], az = pos[ia + 2];
            var bx = pos[ib], by = pos[ib + 1], bz = pos[ib + 2];
            var cx = pos[ic], cy = pos[ic + 1], cz = pos[ic + 2];
            var e1x = bx - ax, e1y = by - ay, e1z = bz - az;
            var L1 = Math.sqrt(e1x * e1x + e1y * e1y + e1z * e1z) || 1;
            e1x /= L1; e1y /= L1; e1z /= L1;
            var acx = cx - ax, acy = cy - ay, acz = cz - az;
            var nx = e1y * acz - e1z * acy, ny = e1z * acx - e1x * acz, nz = e1x * acy - e1y * acx;
            var nl = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
            nx /= nl; ny /= nl; nz /= nl;
            var b2x = ny * e1z - nz * e1y, b2y = nz * e1x - nx * e1z, b2z = nx * e1y - ny * e1x;
            var w1 = st.w1[i], w2 = st.w2[i], w3 = 1 - w1 - w2;
            var r = L1 / st.L0[i];
            var ox = st.off[i * 3] * r, oy = st.off[i * 3 + 1] * r, oz = st.off[i * 3 + 2] * r;
            out[i * 3] = w1 * ax + w2 * bx + w3 * cx + ox * e1x + oy * b2x + oz * nx;
            out[i * 3 + 1] = w1 * ay + w2 * by + w3 * cy + ox * e1y + oy * b2y + oz * ny;
            out[i * 3 + 2] = w1 * az + w2 * bz + w3 * cz + ox * e1z + oy * b2z + oz * nz;
        }
        hairGeo.attributes.position.needsUpdate = true;
        hairGeo.computeVertexNormals();
        hairGeo.computeBoundingBox(); hairGeo.computeBoundingSphere();
    }

    function disposeHair() {
        if (hairMesh) { try { ctx.mesh.remove(hairMesh); hairGeo.dispose(); hairMat.dispose(); } catch (_) {} }
        hairMesh = null; hairGeo = null; hairMat = null;
    }

    function buildHair(st) {
        disposeHair();
        hairGeo = new THREE_.BufferGeometry();
        hairGeo.setAttribute('position', new THREE_.BufferAttribute(new Float32Array(st.nv * 3), 3));
        hairGeo.setIndex(new THREE_.BufferAttribute(new Uint32Array(st.tris), 1));
        hairMat = new THREE_.MeshStandardMaterial({
            color: new THREE_.Color(HAIR_COLORS[state.c] || HAIR_COLORS[1]),
            roughness: 0.62, metalness: 0.05, side: THREE_.DoubleSide
        });
        hairMesh = new THREE_.Mesh(hairGeo, hairMat);
        hairMesh.name = 'hair';
        ctx.mesh.add(hairMesh);      // child of the face mesh → inherits head pose
        rebuild();
    }

    function setStyle(idx, silent) {
        if (idx < 0) {
            state.s = -1; disposeHair(); markUI();
            if (!silent) { ae('fm_hair_set', { id: 'none' }); ctx.requestSave(); }
            return;
        }
        var entry = styles[idx];
        if (!entry) return;
        state.s = idx;
        if (cache[entry.id]) { buildHair(cache[entry.id]); markUI(); if (!silent) { ae('fm_hair_set', { id: entry.id }); ctx.requestSave(); } return; }
        markUI();
        loading++;
        var t0 = performance.now();
        setBusy(true);
        fetchAsset(entry.file, false).then(function (buf) {
            cache[entry.id] = parseStyle(buf, entry);
            loading--; if (!loading) setBusy(false);
            if (state.s === idx) { buildHair(cache[entry.id]); markUI(); }   // last click wins
            ae('fm_hair_load', { id: entry.id, ms: Math.round(performance.now() - t0), bytes: buf.byteLength });
            if (!silent) ctx.requestSave();
        }).catch(function (err) {
            loading--; if (!loading) setBusy(false);
            if (state.s === idx) { state.s = -1; markUI(); }
            try { (window.__toast || function () {})(L('hair_err', 'Could not load the hairstyle. Please try again.'), 'error'); } catch (_) {}
            ae('fm_hair_err', { code: 'style', id: entry.id, msg: (err && err.message || '').slice(0, 60) });
        });
    }

    function setColor(i) {
        state.c = clampColor(i);
        if (hairMat) hairMat.color.set(HAIR_COLORS[state.c]);
        markUI();
        ae('fm_hair_color', { i: state.c });
        ctx.requestSave();
    }

    function setBusy(on) { if (els.grid) els.grid.classList.toggle('fm-hair-busy', !!on); }

    /* ───────── UI ───────── */
    function buildTab() {
        var reg = ctx.registerTab('hair', L('tab_hair', 'Hair'));
        var pane = reg.pane;
        pane.appendChild(el('div', 'fm-gt', L('g_hair_style', 'Hairstyle')));
        var grid = el('div', 'fm-hairgrid');
        // "none" card
        var none = el('button', 'fm-hairbtn fm-hairnone'); none.type = 'button';
        none.setAttribute('aria-label', L('hair_none', 'No hair'));
        none.appendChild(el('span', null, '∅'));
        none.appendChild(el('i', 'fm-hairlbl', L('hair_none', 'None')));
        none.addEventListener('click', function () { setStyle(-1); });
        grid.appendChild(none);
        styles.forEach(function (s, i) {
            var b = el('button', 'fm-hairbtn'); b.type = 'button';
            var nm = L('hair_s_' + s.id, s.id);
            b.setAttribute('aria-label', nm);
            var img = el('img'); img.loading = 'lazy'; img.alt = nm;
            img.src = (base || (ctx.bases && ctx.bases[0]) || '/vendor/gnm/') + 'hair/' + (s.thumb || ('thumb-' + s.id + '.png')) + assetV;
            b.appendChild(img);
            b.appendChild(el('i', 'fm-hairlbl', nm));
            b.addEventListener('click', function () { setStyle(i); });
            grid.appendChild(b);
        });
        pane.appendChild(grid);
        els.grid = grid;

        pane.appendChild(el('div', 'fm-gt', L('g_hair_color', 'Hair color')));
        var row = el('div', 'fm-swatches');
        HAIR_COLORS.forEach(function (hex, i) {
            var b = el('button', 'fm-swatch'); b.type = 'button'; b.style.background = hex;
            b.setAttribute('aria-label', L('g_hair_color', 'Hair color') + ' ' + (i + 1));
            b.addEventListener('click', function () { setColor(i); });
            row.appendChild(b);
        });
        pane.appendChild(row);
        els.colorRow = row;
    }

    function markUI() {
        if (els.grid) Array.prototype.forEach.call(els.grid.children, function (b, i) {
            b.classList.toggle('active', i === state.s + 1);   // [0] is "none"
        });
        if (els.colorRow) Array.prototype.forEach.call(els.colorRow.children, function (b, i) {
            b.classList.toggle('active', i === state.c);
        });
    }

    /* ───────── hooks for app.js ───────── */
    var api = {
        getState: function () { return { s: state.s, c: state.c }; },
        applyState: function (st) {
            if (!st) return;
            state.c = clampColor(st.c);
            if (hairMat) hairMat.color.set(HAIR_COLORS[state.c]);
            if (typeof st.s === 'number' && st.s !== state.s) setStyle(st.s, true); else markUI();
        },
        reset: function () { state = { s: -1, c: 1 }; disposeHair(); markUI(); },
        expandBox: function (bb) { if (hairGeo && hairGeo.boundingBox) bb.union(hairGeo.boundingBox); },
        exportGeometry: function () {
            if (state.s < 0 || !hairGeo) return null;
            hairGeo.computeVertexNormals();
            var col = hairMat ? hairMat.color : { r: 0.2, g: 0.15, b: 0.1 };
            return {
                pos: hairGeo.attributes.position.array,
                normal: hairGeo.attributes.normal.array,
                tris: hairGeo.index.array,
                nv: hairGeo.attributes.position.count,
                nt: hairGeo.index.count / 3,
                color: [col.r, col.g, col.b]
            };
        },
        setHair: function (s, c) { state.c = clampColor(c); setStyle(s, false); },  // test hook
        styles: function () { return styles.map(function (s) { return s.id; }); },
        active: function () { return !!hairMesh; }
    };
})();
